import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from "@material-ui/core"
import { ThemeProvider, makeStyles } from "@material-ui/core/styles"
import DateFnsUtils from "@date-io/date-fns"
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers"
import {
    Check,
    Event,
    LocalOffer,
    Payment,
    Person,
    Search,
    Storefront,
} from "@material-ui/icons"
import { PLATAFORMAS, SITUACOES, TIPOS_FORMA_PAGAMENTO, TIPOS_PEDIDO } from "./constants"

// Verde fixo da tela (não segue a cor do layout do estabelecimento).
export const VERDE = "#4caf50"
export const VERDE_ESCURO = "#388e3c"
const VERDE_CLARO = "#e8f5e9"

// Tema local: chips, checkboxes, seletores de data e o foco dos campos usam o verde fixo.
const temaVerde = externo => ({
    ...externo,
    palette: {
        ...externo.palette,
        primary: { light: "#80e27e", main: VERDE, dark: VERDE_ESCURO, contrastText: "#fff" },
        secondary: { light: "#80e27e", main: VERDE, dark: VERDE_ESCURO, contrastText: "#fff" },
    },
})

const useStyles = makeStyles({
    painel: {
        padding: 20,
        borderRadius: 12,
        border: "1px solid #e5e7eb",
        backgroundColor: "#fafbfc",
    },
    secao: {
        backgroundColor: "white",
        border: "1px solid #eef0f3",
        borderRadius: 10,
        padding: 16,
        height: "100%",
    },
    tituloSecao: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        color: "#4b5563",
        marginBottom: 12,
        "& svg": { fontSize: 18, color: VERDE },
    },
    rotulo: {
        display: "block",
        fontSize: 12,
        fontWeight: 600,
        color: "#6b7280",
        marginBottom: 6,
    },
    // "&&" sobe a especificidade acima do estilo padrão do Chip (clickable/hover/focus).
    chip: {
        "&&": {
            borderRadius: 999,
            fontWeight: 500,
            border: "1px solid #d1d5db",
            backgroundColor: "white",
            color: "#374151",
        },
        "&&:hover, &&:focus": { backgroundColor: VERDE_CLARO, borderColor: VERDE },
    },
    chipAtivo: {
        "&&": {
            borderRadius: 999,
            fontWeight: 600,
            border: `1px solid ${VERDE}`,
            backgroundColor: "var(--cor-botao-salvar)",
            color: "white",
        },
        "&&:hover, &&:focus": { backgroundColor: "var(--cor-botao-salvar-hover)" },
        "&& svg": { color: "white" },
    },
    rodape: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 8,
        marginTop: 16,
        flexWrap: "wrap",
    },
    btnLimpar: {
        textTransform: "none",
        color: "#6b7280",
        borderRadius: 8,
        fontWeight: 500,
    },
    btnAplicar: {
        textTransform: "none",
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        fontWeight: 600,
        borderRadius: 8,
        padding: "8px 20px",
        boxShadow: "0 2px 8px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
})

const Secao = ({ icone, titulo, children }) => {
    const classes = useStyles()
    return (
        <Box className={classes.secao}>
            <Typography component="div" className={classes.tituloSecao}>
                {icone}
                {titulo}
            </Typography>
            {children}
        </Box>
    )
}

// Seleção múltipla (ou única, com `unico`) em chips; selecionado = verde com ✓.
const Chips = ({ label, options, value, onChange, unico = false }) => {
    const classes = useStyles()
    const ativo = v => (unico ? value === v : value.includes(v))
    const alternar = v => {
        if (unico) return onChange(v)
        onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v])
    }
    return (
        <Box mb={1.5}>
            <span className={classes.rotulo}>{label}</span>
            <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                {options.map(opt => (
                    <Chip
                        key={String(opt.value)}
                        label={opt.label}
                        size="small"
                        clickable
                        icon={ativo(opt.value) ? <Check fontSize="small" /> : undefined}
                        className={ativo(opt.value) ? classes.chipAtivo : classes.chip}
                        onClick={() => alternar(opt.value)}
                    />
                ))}
            </Box>
        </Box>
    )
}

const OPCOES_NFCE = [
    { value: null, label: "Todos" },
    { value: true, label: "Com NFC-e" },
    { value: false, label: "Sem NFC-e" },
]

export default function Filtros({
    filtros,
    setFiltros,
    formasPagamento,
    estabelecimentos,
    onLimpar,
    onAplicar,
    loading,
}) {
    const classes = useStyles()
    const setCampo = (campo, valor) => setFiltros(prev => ({ ...prev, [campo]: valor }))
    const campo = (nome, label, extra = {}) => (
        <TextField
            label={label}
            variant="outlined"
            size="small"
            value={filtros[nome] ?? ""}
            onChange={e => setCampo(nome, e.target.value)}
            onKeyDown={e => {
                if (e.key === "Enter") onAplicar()
            }}
            fullWidth
            {...extra}
        />
    )

    return (
        <ThemeProvider theme={temaVerde}>
            <Paper elevation={0} className={classes.painel}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Secao icone={<Event />} titulo="Período">
                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <KeyboardDateTimePicker
                                            label="Data início"
                                            inputVariant="outlined"
                                            size="small"
                                            ampm={false}
                                            format="dd/MM/yyyy HH:mm"
                                            value={filtros.dataInicio}
                                            onChange={d => setCampo("dataInicio", d)}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <KeyboardDateTimePicker
                                            label="Data fim"
                                            inputVariant="outlined"
                                            size="small"
                                            ampm={false}
                                            format="dd/MM/yyyy HH:mm"
                                            value={filtros.dataFim}
                                            onChange={d => setCampo("dataFim", d)}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </MuiPickersUtilsProvider>
                        </Secao>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Secao icone={<Person />} titulo="Cliente">
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    {campo("nomeCliente", "Nome do cliente")}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {campo("telefone", "Telefone")}
                                </Grid>
                            </Grid>
                        </Secao>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Secao icone={<Search />} titulo="Pedido">
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    {campo("codigoPedido", "Código", { type: "number" })}
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    {campo("senha", "Senha", {
                                        placeholder: "Ex.: 42",
                                        InputLabelProps: { shrink: true },
                                    })}
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    {campo("cupomDesconto", "Cupom")}
                                </Grid>
                            </Grid>
                            {estabelecimentos?.length > 1 && (
                                <Box mt={2}>
                                    <FormControl variant="outlined" size="small" fullWidth>
                                        <InputLabel>Estabelecimento</InputLabel>
                                        <Select
                                            value={filtros.estabelecimentoFiltro || ""}
                                            onChange={e =>
                                                setCampo("estabelecimentoFiltro", e.target.value || "")
                                            }
                                            label="Estabelecimento"
                                            startAdornment={
                                                <Storefront
                                                    fontSize="small"
                                                    style={{ color: "#9ca3af", marginRight: 6 }}
                                                />
                                            }
                                        >
                                            <MenuItem value="">
                                                <em>Todos</em>
                                            </MenuItem>
                                            {estabelecimentos.map(est => (
                                                <MenuItem key={est.id} value={est.id}>
                                                    {est.nome || est.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}
                        </Secao>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Secao icone={<Payment />} titulo="Pagamento">
                            <Box mb={2}>
                                <FormControl variant="outlined" size="small" fullWidth>
                                    <InputLabel>Formas de pagamento</InputLabel>
                                    <Select
                                        multiple
                                        value={filtros.idsFormasPagamento}
                                        onChange={e => setCampo("idsFormasPagamento", e.target.value)}
                                        renderValue={selected =>
                                            (formasPagamento || [])
                                                .filter(fp => selected.includes(fp.id))
                                                .map(fp => fp.nome || fp.descricao)
                                                .join(", ") || ""
                                        }
                                        label="Formas de pagamento"
                                    >
                                        {(formasPagamento || []).map(fp => (
                                            <MenuItem key={fp.id} value={fp.id}>
                                                <Checkbox
                                                    color="primary"
                                                    size="small"
                                                    checked={filtros.idsFormasPagamento.includes(fp.id)}
                                                />
                                                <ListItemText primary={fp.nome || fp.descricao} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Chips
                                label="Tipo de forma de pagamento"
                                options={TIPOS_FORMA_PAGAMENTO}
                                value={filtros.tiposFormaPagamento}
                                onChange={v => setCampo("tiposFormaPagamento", v)}
                            />
                            <Chips
                                label="Nota fiscal (NFC-e)"
                                options={OPCOES_NFCE}
                                value={filtros.temNotaFiscal}
                                onChange={v => setCampo("temNotaFiscal", v)}
                                unico
                            />
                        </Secao>
                    </Grid>

                    <Grid item xs={12}>
                        <Secao icone={<LocalOffer />} titulo="Origem e situação">
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <Chips
                                        label="Tipo de pedido"
                                        options={TIPOS_PEDIDO}
                                        value={filtros.tiposPedido}
                                        onChange={v => setCampo("tiposPedido", v)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Chips
                                        label="Plataforma"
                                        options={PLATAFORMAS}
                                        value={filtros.plataformas}
                                        onChange={v => setCampo("plataformas", v)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Chips
                                        label="Situação"
                                        options={SITUACOES}
                                        value={filtros.situacoes}
                                        onChange={v => setCampo("situacoes", v)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box display="flex" flexWrap="wrap" style={{ columnGap: 24 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    size="small"
                                                    checked={!!filtros.exibirPedidosNaoConfirmados}
                                                    onChange={e =>
                                                        setCampo("exibirPedidosNaoConfirmados", e.target.checked)
                                                    }
                                                />
                                            }
                                            label="Exibir pedidos não aprovados"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    size="small"
                                                    checked={!!filtros.exibirExpirados}
                                                    onChange={e => setCampo("exibirExpirados", e.target.checked)}
                                                />
                                            }
                                            label="Exibir pedidos expirados"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Secao>
                    </Grid>
                </Grid>

                <Box className={classes.rodape}>
                    <Button onClick={onLimpar} disabled={loading} className={classes.btnLimpar}>
                        Limpar filtros
                    </Button>
                    <Button
                        onClick={onAplicar}
                        disabled={loading}
                        className={classes.btnAplicar}
                        startIcon={<Search />}
                    >
                        Aplicar filtros
                    </Button>
                </Box>
            </Paper>
        </ThemeProvider>
    )
}
